import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { loadState } from '../localStorage';
import { ReportForm, RefreshToken } from '../../../flow/components';
import { dataURLtoFile } from '../formatter';


const env = process.env.REACT_APP_CUSTOM_NODE_ENV || 'development';

let returnError;

console.log('Using enviroment: ', env);

const api = axios.create({
  baseURL: {
    development: 'http://localhost:3333',
    //development: 'http://192.168.1.54:3333',
    production: 'https://locall.ansof.com.br',
    staging: 'https://staging.locall.ansof.com.br',
  }[env],
});

api.interceptors.request.use(
  async (config) => {
    const requestConfig = config;
    let accessToken = null;
    let userId = null;

    try {
      const persitedState = loadState();
      accessToken = persitedState.AuthReducer.token;
      userId = persitedState.AuthReducer.userId;
    } catch (e) {
      accessToken = null;
      userId = null;
    }

    if (accessToken && userId) {
      requestConfig.headers['x-access-token'] = requestConfig.headers['x-access-token'] || accessToken;
      requestConfig.headers['user-id'] = requestConfig.headers['user-id'] || userId;
    }

    return Promise.resolve(requestConfig);
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  ({ data }) => Promise.resolve(data),

  (error) => {
    if (returnError) return Promise.reject(error);
    if ([
      'email-or-whatsapp-or-userName-already-exists',
      'validation/no-user-found',
    ].includes(error.response?.data.code)) return Promise.reject(error);

    if (error.response?.data.code === 'token/wrong-token') {
      toast(
        (<RefreshToken request={error.response?.config} response={error.response?.data || { statusCode: 500 }} />),
        { type: toast.TYPE.INFO, autoClose: false },
      );
      return Promise.reject(error);
    }

    toast(
      (<ReportForm error={error || { statusCode: 500 }} />),
      { type: toast.TYPE.INFO, autoClose: false, position: 'bottom-right' },
    );
    return Promise.reject(error);
  },
);

export default api;

export const UserService = (needError) => {
  returnError = needError;
  return ({
    getByIds: (ids = []) => api.get('users', { params: { usersIds: ids } }),

    getByEmail: (email) => api.get('users', { params: { emails: [email] } }),

    getByWhatsapp: (whatsapp) => api.get('users', { params: { whatsapps: [whatsapp] } }),

    getByName: (userName) => api.get(`users/${userName}`),

    create: ({
      firstName, lastName, email, whatsapp, password = '12345678', forceUserName = false,
    }) => {
      const data = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email,
        whatsapp,
        userName: (`${firstName}${lastName}`).normalize('NFD').replace(/\s|[\u0300-\u036f]/g, '').toLowerCase(),
        password,
        forceUserName,
      };
      return api.post('users', data);
    },

    login: ({ email, password = '12345678' }) => api.post('session', { email, password }),

    updatePassword: ({ token, userId, password = '12345678' }) => api.patch(
    `users/${userId}/password`,
    { password },
    { headers: { 'x-access-token': token } },
    ),

    update: ({ body }) => api.put('users', body),

    getAddresses: (userId) => api.get(`addresses/?userId=${userId}`),

    createAddress: ({ body }) => api.post('addresses', body),

    retrievePassword: (email) => api.post('/credentials/passwordRetrieval', { email }), // returns no body

    deleteAddress: (id) => api.delete(`addresses/${id}`),
  });
};

export const SpaceService = (needError) => {
  returnError = needError;
  return ({
    create: (body) => api.post('/v1/producers', body),
    getByIds: (ids = [], deletedOnes = false) => api.get('/v2/producers', { params: { ids, deletedOnes } }),
    getAllGeocoded: () => api.get('/v2/producers', { params: { status: ['active', 'inactive'], geocoded: true } }),
    getAddresses: (producerId) => api.get('/v1/addresses', { params: { addressableTypes: 'producer', addressablesIds: [producerId] } }),
    createAddress: (body) => api.post('/v1/addresses', body),
    getByNicknames: (nicknames = []) => api.get('/v2/producers', { params: { nicknames, geocoded: true } }),
    getByUserId: (userId) => api.get('/v2/producers', { params: { userId } }),
    remove: (producerId) => api.delete(`/producers/${producerId}`),
  });
};

export const AddressService = (needError) => {
  returnError = needError;
  return ({
    getAddresses: (addressableType, addressableId) => api.get('/v1/addresses', { params: { addressableTypes: addressableType, addressablesIds: [addressableId] } }),
    createAddress: (body) => api.post('/v1/addresses', body),
    deleteAddress: (id) => api.delete(`addresses/${id}`),
  });
};

export const OfferService = (needError) => {
  returnError = needError;
  return ({
    getByIds: (ids = []) => api.get('v1/offers', { params: { offersIds: ids } }),
    getByProducerId: (producerId) => api.get('v1/offers', { params: { producerId } }),
    getByNickname: (nickname) => api.get(`v1/offers/?nickname=${nickname}`),
  });
};

export const OrderService = (needError) => {
  returnError = needError;
  return ({
    create: ({ body }) => api.post('orders', body),
    getByUserId: () => api.get('v2/orders?type=requested'),
    getOrderPix: (orderId)=> api.get(`orders/${orderId}/pix`)
  });
};

export const DeliveryService = (needError) => {
  returnError = needError;
  return ({
    getDistancesAndValues: (producerId, addresses = []) => api.get(
      '/v1/deliveries/simulatedValues',
      { params: { producerId, destinations: addresses } },
    ),
  });
};

export const IssuesService = (needError) => {
  returnError = needError;
  return ({
    create: (user, message) => api.post('/issues', { user, message }),
  });
};

export const ImageService = (needError) => {
  returnError = needError;
  return ({
    uploadImage: async ({ dataUrl, imageable_type, imageable_id }) => {
      const file = await dataURLtoFile(dataUrl, 'photo');
      const data = new FormData();
      data.append('image', file);

      const header = {
        headers: {
          'imageable-type': imageable_type,
          'content-type': 'multipart/form-data',
        },
      };
      if (imageable_type !== 'user') header.headers = { ...header.headers, 'imageable-id': imageable_id };
      const response = await api.post('/images/', data, header);
      return response.data;
    },
  });
};
